// ============================================================================
// BP-04-02: Documents Frontend - useFolders Hook
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { foldersApi } from '../api/documentsApi';
import { useDocumentsStore } from '../stores/documentsStore';
import type { DocumentFolder, DocumentFolderCreate, DocumentFolderUpdate } from '../types/documentsTypes';
import { documentKeys } from './useDocuments';

// -----------------------------------------------------------------------------
// Query Keys
// -----------------------------------------------------------------------------

export const folderKeys = {
  all: ['folders'] as const,
  lists: () => [...folderKeys.all, 'list'] as const,
  list: (familyId: string) => [...folderKeys.lists(), familyId] as const,
  details: () => [...folderKeys.all, 'detail'] as const,
  detail: (id: string) => [...folderKeys.details(), id] as const,
};

// -----------------------------------------------------------------------------
// List Folders Hook
// -----------------------------------------------------------------------------

interface UseFoldersOptions {
  familyId: string;
  enabled?: boolean;
}

export function useFolders({ familyId, enabled = true }: UseFoldersOptions) {
  const { setCachedFolders } = useDocumentsStore();

  return useQuery({
    queryKey: folderKeys.list(familyId),
    queryFn: async () => {
      const folders = await foldersApi.list(familyId);
      setCachedFolders(folders);
      return folders;
    },
    enabled: enabled && !!familyId,
    staleTime: 60 * 1000, // 1 minute
  });
}

// -----------------------------------------------------------------------------
// Single Folder Hook
// -----------------------------------------------------------------------------

export function useFolder(folderId: string, enabled = true) {
  return useQuery({
    queryKey: folderKeys.detail(folderId),
    queryFn: () => foldersApi.get(folderId),
    enabled: enabled && !!folderId,
    staleTime: 60 * 1000,
  });
}

// -----------------------------------------------------------------------------
// Folder Mutations
// -----------------------------------------------------------------------------

export function useCreateFolder(familyId: string) {
  const queryClient = useQueryClient();
  const { closeCreateFolder, expandFolder } = useDocumentsStore();

  return useMutation({
    mutationFn: (data: DocumentFolderCreate) => foldersApi.create(familyId, data),
    onSuccess: (newFolder) => {
      // Invalidate folder list
      queryClient.invalidateQueries({ queryKey: folderKeys.list(familyId) });
      // Add to cache
      queryClient.setQueryData(folderKeys.detail(newFolder.id), newFolder);
      // Expand parent folder if exists
      if (newFolder.parent_id) {
        expandFolder(newFolder.parent_id);
      }
      // Close modal
      closeCreateFolder();
    },
  });
}

export function useUpdateFolder(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, data }: { folderId: string; data: DocumentFolderUpdate }) =>
      foldersApi.update(folderId, data),
    onSuccess: (updatedFolder) => {
      queryClient.setQueryData(folderKeys.detail(updatedFolder.id), updatedFolder);
      queryClient.invalidateQueries({ queryKey: folderKeys.list(familyId) });
    },
  });
}

export function useDeleteFolder(familyId: string) {
  const queryClient = useQueryClient();
  const { currentFolderId, setCurrentFolderId } = useDocumentsStore();

  return useMutation({
    mutationFn: (folderId: string) => foldersApi.delete(folderId),
    onSuccess: (_, folderId) => {
      // If we deleted the current folder, go to root
      if (currentFolderId === folderId) {
        setCurrentFolderId(null);
      }
      queryClient.removeQueries({ queryKey: folderKeys.detail(folderId) });
      queryClient.invalidateQueries({ queryKey: folderKeys.list(familyId) });
      // Documents might have moved, invalidate them too
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

export function useMoveFolder(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderId,
      targetParentId,
    }: {
      folderId: string;
      targetParentId: string | null;
    }) => foldersApi.move(folderId, targetParentId),
    onSuccess: (updatedFolder) => {
      queryClient.setQueryData(folderKeys.detail(updatedFolder.id), updatedFolder);
      queryClient.invalidateQueries({ queryKey: folderKeys.list(familyId) });
    },
  });
}

// -----------------------------------------------------------------------------
// Folder Tree Utilities
// -----------------------------------------------------------------------------

/**
 * Build a flat array of folders into a tree structure
 */
export function buildFolderTree(folders: DocumentFolder[]): DocumentFolder[] {
  const folderMap = new Map<string, DocumentFolder>();
  const rootFolders: DocumentFolder[] = [];

  // First pass: create map and initialize children arrays
  folders.forEach((folder) => {
    folderMap.set(folder.id, { ...folder, children: [] });
  });

  // Second pass: build tree
  folders.forEach((folder) => {
    const folderWithChildren = folderMap.get(folder.id)!;
    if (folder.parent_id && folderMap.has(folder.parent_id)) {
      const parent = folderMap.get(folder.parent_id)!;
      parent.children = parent.children || [];
      parent.children.push(folderWithChildren);
    } else {
      rootFolders.push(folderWithChildren);
    }
  });

  // Sort by display_order at each level
  const sortFolders = (folders: DocumentFolder[]): DocumentFolder[] => {
    return folders
      .sort((a, b) => a.display_order - b.display_order)
      .map((folder) => ({
        ...folder,
        children: folder.children ? sortFolders(folder.children) : [],
      }));
  };

  return sortFolders(rootFolders);
}

/**
 * Get all folder IDs in the tree (for expand all)
 */
export function getAllFolderIds(folders: DocumentFolder[]): string[] {
  const ids: string[] = [];
  
  const traverse = (folder: DocumentFolder) => {
    ids.push(folder.id);
    if (folder.children) {
      folder.children.forEach(traverse);
    }
  };
  
  folders.forEach(traverse);
  return ids;
}

/**
 * Get the breadcrumb path for a folder
 */
export function getFolderBreadcrumb(
  folderId: string | null,
  folders: DocumentFolder[]
): DocumentFolder[] {
  if (!folderId) return [];

  const path: DocumentFolder[] = [];
  const folderMap = new Map<string, DocumentFolder>();

  // Build a flat map
  const flatten = (folder: DocumentFolder) => {
    folderMap.set(folder.id, folder);
    if (folder.children) {
      folder.children.forEach(flatten);
    }
  };
  folders.forEach(flatten);

  // Walk up the tree
  let currentId: string | null = folderId;
  while (currentId) {
    const folder = folderMap.get(currentId);
    if (folder) {
      path.unshift(folder);
      currentId = folder.parent_id;
    } else {
      break;
    }
  }

  return path;
}

/**
 * Find a folder by ID in the tree
 */
export function findFolderById(
  folderId: string,
  folders: DocumentFolder[]
): DocumentFolder | null {
  for (const folder of folders) {
    if (folder.id === folderId) {
      return folder;
    }
    if (folder.children) {
      const found = findFolderById(folderId, folder.children);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Get all descendant IDs for a folder
 */
export function getDescendantIds(folder: DocumentFolder): string[] {
  const ids: string[] = [];
  
  const traverse = (f: DocumentFolder) => {
    ids.push(f.id);
    if (f.children) {
      f.children.forEach(traverse);
    }
  };
  
  if (folder.children) {
    folder.children.forEach(traverse);
  }
  
  return ids;
}

// -----------------------------------------------------------------------------
// Export
// -----------------------------------------------------------------------------






